import React, { useState, useEffect, useRef, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useBooking } from "../../context/BookingContext";
import { Link } from "react-router-dom";
import ReactModal from "react-modal";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import moment from "moment";
import "./MyBookings.css";

ReactModal.setAppElement("#root");

const MyBookings = () => {
  const { user, loading: authLoading } = useContext(AuthContext);
  const { fetchUserBookings } = useBooking();
  const [bookings, setBookings] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const invoiceRef = useRef();

  useEffect(() => {
    const fetchBookings = async () => {
      if (user?.userID) {
        try {
          const userBookings = await fetchUserBookings();
          setBookings(userBookings.reverse());
        } catch (error) {
          console.error("Error fetching bookings:", error);
          setBookings([]);
        }
      }
    };
    fetchBookings();
  }, [fetchUserBookings, user?.userID]);

  const openModal = (booking) => {
    setSelectedBooking(booking);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedBooking(null);
    setIsModalOpen(false);
  };

  const handleDownloadPDF = () => {
    const input = invoiceRef.current;

    input.style.width = "800px"; 
    input.style.padding = "25px"; 

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      logging: false, 
    })
      .then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const imgProps = pdf.getImageProperties(imgData);
        const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

        // Ensure the content fits within A4 page
        if (imgHeight > pdfHeight) {
          const ratio = pdfHeight / imgHeight;
          pdf.addImage(imgData, "PNG", 0, 0, pdfWidth * ratio, pdfHeight);
        } else {
          pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, imgHeight);
        }

        pdf.save(`invoice_${selectedBooking.BookingID}.pdf`);

        // Reset styles after PDF generation
        input.style.width = "";
        input.style.padding = "";
      })
      .catch((error) => {
        console.error("Error generating PDF:", error);
      });
  };

  if (authLoading) return <div className="loading-state">Loading...</div>;

  if (!user)
    return (
      <div className="loading-state">
        Please log in or sign up to view bookings
      </div>
    );

  return (
    <div className="my-bookings-container">
      <h1>My Bookings</h1>
      {!bookings || bookings.length === 0 ? (
        <div className="no-data">No bookings found</div>
      ) : (
        <div className="bookings-grid">
          {bookings.map((booking) => (
            <div key={booking.BookingID} className="booking-card">
              <div className="card-header">
                <h3>
                  <Link to={`/kitchen/${booking.KitchenID}`}>
                    {booking.KitchenTitle}
                  </Link>
                </h3>
                <span
                  className={`status-badge status-${booking.Status?.toLowerCase()}`}
                >
                  {booking.Status}
                </span>
              </div>

              <div className="card-content">
                <div className="time-section">
                  <div className="time-item">
                    <span className="label">Check-in</span>
                    <span className="value">
                      {moment
                        .utc(booking.CheckinTimestamp)
                        .local()
                        .format("MMM DD, YYYY HH:mm")}
                    </span>
                  </div>
                  <div className="time-item">
                    <span className="label">Check-out</span>
                    <span className="value">
                      {moment
                        .utc(booking.CheckoutTimestamp)
                        .local()
                        .format("MMM DD, YYYY HH:mm")}
                    </span>
                  </div>
                </div>

                <div className="details-section">
                  <div className="detail-item">
                    <span className="label">Duration</span>
                    <span className="value">
                      {booking.BookingType === "hour"
                        ? `${booking.TotalHours} hr${
                            booking.TotalHours > 1 ? "s" : ""
                          }`
                        : `${booking.TotalDays || 1} day${
                            (booking.TotalDays || 1) > 1 ? "s" : ""
                          }`}
                    </span>
                  </div>
                </div>

                <div className="price-section">
                  <div className="price-item">
                    <span className="label">Base</span>
                    <span className="value">
                      ${booking.BaseAmount?.toFixed(2)}
                    </span>
                  </div>
                  <div className="price-item">
                    <span className="label">Fees</span>
                    <span className="value">
                      ${booking.ServiceFee?.toFixed(2)}
                    </span>
                  </div>
                  {booking.SecurityDeposit > 0 && (
                    <div className="price-item">
                      <span className="label">Deposit</span>
                      <span className="value">
                        ${booking.SecurityDeposit.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="price-item total">
                    <span className="label">Total</span>
                    <span className="value">
                      ${booking.TotalAmount?.toFixed(2)}
                    </span>
                  </div>
                </div>

                <button
                  className="invoice-btn"
                  onClick={() => openModal(booking)}
                >
                  View Invoice
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedBooking && (
        <ReactModal
          isOpen={isModalOpen}
          onRequestClose={closeModal}
          contentLabel="Invoice Modal"
          className="booking-modal"
          overlayClassName="booking-modal-overlay"
        >
          <div className="booking-modal-content" ref={invoiceRef}>
            <div className="booking-modal-header">
              <h2>Invoice #{selectedBooking.BookingID}</h2>
            </div>
            <div className="booking-modal-info-container">
              <div className="booking-modal-company-info">
                <h3>AirKitchenz Inc.</h3>
                <p>Email: contact@airkitchenz.com</p>
                <p>Invoice Date: {moment().format("YYYY-MM-DD")}</p>
              </div>
              <div className="booking-modal-customer-info">
                <h4>Bill To:</h4>
                <p>
                  <strong>Name:</strong> {user.firstName} {user.lastName}
                </p>
                <p>
                  <strong>Email:</strong> {user.email}
                </p>
                <p>
                  <strong>Phone:</strong> {user.phone || "N/A"}
                </p>
              </div>
            </div>
            <table className="booking-modal-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Check-in</th>
                  <th>Check-out</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{selectedBooking.KitchenTitle || "Kitchen Booking"}</td>
                  <td>
                    {moment
                      .utc(selectedBooking.CheckinTimestamp)
                      .local()
                      .format("YYYY-MM-DD HH:mm")}
                  </td>
                  <td>
                    {moment
                      .utc(selectedBooking.CheckoutTimestamp)
                      .local()
                      .format("YYYY-MM-DD HH:mm")}
                  </td>
                  <td>
                    {selectedBooking.BookingType?.toLowerCase() === "hour"
                      ? `${selectedBooking.TotalHours} ${
                          selectedBooking.TotalHours > 1 ? "hours" : "hour"
                        }`
                      : `${selectedBooking.TotalDays || 1} ${
                          (selectedBooking.TotalDays || 1) > 1 ? "days" : "day"
                        }`}
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="booking-modal-cost-breakdown">
              <h4>Cost Breakdown</h4>
              <div className="cost-breakdown-items">
                <div className="cost-breakdown-item">
                  <span>Base Amount:</span>
                  <span>
                    ${selectedBooking.BaseAmount?.toFixed(2) || "0.00"}
                  </span>
                </div>
                <div className="cost-breakdown-item">
                  <span>Service Fee:</span>
                  <span>
                    ${selectedBooking.ServiceFee?.toFixed(2) || "0.00"}
                  </span>
                </div>
                {selectedBooking.SecurityDeposit > 0 && (
                  <div className="cost-breakdown-item">
                    <span>Security Deposit:</span>
                    <span>${selectedBooking.SecurityDeposit.toFixed(2)}</span>
                  </div>
                )}
                <div className="cost-breakdown-item total">
                  <span>Total Amount:</span>
                  <span>
                    ${selectedBooking.TotalAmount?.toFixed(2) || "0.00"}
                  </span>
                </div>
              </div>
            </div>
            <div className="booking-modal-instructions">
              <h4>Instructions</h4>
              <p>
                If you need to cancel your booking, please contact us at least
                24 hours in advance to receive a full refund. For more details,
                contact us at{" "}
                <a
                  href="mailto:contact@airkitchenz.com?subject=Inquiry&body=Hi, I would like to contact regarding a booking."
                  className="booking-modal-link"
                >
                  contact@airkitchenz.com
                </a>
              </p>
            </div>
          </div>
          <div className="booking-modal-footer">
            <button
              className="booking-modal-download"
              onClick={handleDownloadPDF}
            >
              Download PDF
            </button>
            <button className="booking-modal-close" onClick={closeModal}>
              Close
            </button>
          </div>
        </ReactModal>
      )}
    </div>
  );
};

export default MyBookings;
